import { MdInfoOutline } from 'react-icons/md';
import { isProductAvailable } from '@/lib/data-transformer/productData';
import { ProductItem } from '@/lib/types/api';
import { currencyFormat } from '@/lib/data-transformer/gindumacUtils';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next-translate-routes-multi-domain';
import { Dialog } from '@headlessui/react';
import { useState } from 'react';
import { ProductPaymentTerms } from './ProductPaymentTerms';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setIsGindumacPriceModalOpen } from '../forms/NewsLetter/modalSlice';
import { AppState } from '@/redux/store';
import classNames from 'classnames';
import { RentingModalButton, RentintModal } from './ProductRenting';
import useIsMobile from '@/lib/hooks/useIsMobile';
import { ArrowDownIcon } from '@heroicons/react/solid';

export function ProductPaymentInfo({
  product,
  priceClass,
  onlyModalButton,
}: {
  product: ProductItem;
  priceClass?: string;
  onlyModalButton?: boolean;
}) {
  const { t } = useTranslation();
  const isAvailable = product ? isProductAvailable(product) : false;
  const { locale } = useRouter();
  return isAvailable ? (
    <div className='col s12'>
      <div className='mx-auto flex items-center justify-between'>
        <div className='flex flex-col items-start justify-start'>
          <div className={'mb-2 text-left font-secondary font-light'}>
            <span className={priceClass}>
              {currencyFormat(product.price, locale)}
            </span>
          </div>
          {onlyModalButton ? <PriceModalButton /> : <GindumacPriceModal />}
          <div className='text-left'>{t('seo:DETAIL_PAGE_EX_WORKS')}</div>
        </div>
        <div className='flex flex-col'>
          <RentintModal product={product} />
          <PaymentOptionsModal />
        </div>
      </div>
    </div>
  ) : null;
}

export function ProductPaymentInfoHero({
  product,
  priceClass,
}: {
  product: ProductItem;
  priceClass?: string;
}) {
  const { t } = useTranslation();
  const isAvailable = product ? isProductAvailable(product) : false;
  const { locale } = useRouter();
  return isAvailable ? (
    <div className='col s12'>
      <div className='mx-auto flex items-center justify-between'>
        <div className='flex flex-col items-start justify-start'>
          <div className={'text-left font-secondary font-light'}>
            <span className={priceClass}>
              {currencyFormat(product.price, locale)}
            </span>
          </div>
          <RentingModalButton product={product} isInHero />
          <PriceModalButton />
          <div className='text-left'>{t('seo:DETAIL_PAGE_EX_WORKS')}</div>
        </div>
      </div>
    </div>
  ) : null;
}

function PriceModalButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  return (
    <div
      className='flex cursor-pointer items-center text-left uppercase hover:underline'
      onClick={() => dispatch(setIsGindumacPriceModalOpen(true))}
    >
      {t('seo:DETAIL_PAGE_GINDUMAC_PRICE')}
      <MdInfoOutline className='text-xl' />
    </div>
  );
}

function GindumacPriceModal() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isGindumacPriceModalOpen } = useAppSelector(
    (state: AppState) => state.modals
  );
  const isMobile = useIsMobile();

  return (
    <section>
      <PriceModalButton />
      <style jsx>{`
        label {
          text: white;
        }
      `}</style>
      <Dialog
        open={isGindumacPriceModalOpen}
        onClose={() => dispatch(setIsGindumacPriceModalOpen(false))}
        className='relative z-50'
      >
        <div className='fixed inset-0 bg-black/70' aria-hidden='true' />

        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <Dialog.Panel className='w-full max-w-5xl rounded bg-white p-4'>
            <Dialog.Title className='flex items-center justify-between'>
              <h1 className='mx-auto my-4 text-center'>
                {t('seo:DETAIL_MODALPRICEINFO_HEADER')}
              </h1>
              <button className='relative bottom-4'>
                {/* close icon */}
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-6 w-6 text-brand-500'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  onClick={() => dispatch(setIsGindumacPriceModalOpen(false))}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </Dialog.Title>
            <div>
              <div>
                <div>
                  <div
                    className={classNames(
                      'mx-auto w-full border-b-2 py-2 text-center uppercase text-brand-500'
                    )}
                    dangerouslySetInnerHTML={{
                      __html: isMobile
                        ? t('products:DETAIL_MODALPRICEINFO_TAB1_MOBILE')
                        : t('seo:DETAIL_MODALPRICEINFO_TAB1'),
                    }}
                  />
                </div>
                <div className='pt-4 text-brand-500'>
                  <div>{t('seo:DETAIL_MODALPRICEINFO_TAB1_CONTENT')}</div>
                </div>
              </div>
              <div className='mb-8 px-8 text-center' />
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </section>
  );
}

function PaymentOptionsModal() {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <section>
      <button
        className='border-[1px] border-gray-500 px-4 py-2 text-lg font-extralight uppercase text-gray-500'
        onClick={() => setIsOpen(true)}
      >
        {t('seo:DETAIL_ASIDE_BTN_PAYMENT_OPTIONS')}
      </button>
      <style jsx>{`
        label {
          text: white;
        }
      `}</style>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className='relative z-50'
      >
        <div className='fixed inset-0 bg-black/70' aria-hidden='true' />

        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <Dialog.Panel
            className='max-h-[80vh] w-full max-w-5xl overflow-y-auto rounded bg-white p-4 lg:p-8 lg:py-12'
            id='payment-info-modal'
          >
            <Dialog.Title className='flex items-center justify-between'>
              <h1 className='mx-auto my-4 text-center'>
                {t('products:DETAIL_MODALPRICE_HEADER')}
              </h1>
              <button className='relative bottom-4'>
                {/* close icon */}
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-6 w-6 text-brand-500'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  onClick={() => setIsOpen(false)}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </Dialog.Title>
            <div>
              <div
                className='mb-8 px-8 text-center'
                dangerouslySetInnerHTML={{
                  __html: t('products:DETAIL_MODALPRICE_TAB1_CONTENT'),
                }}
              />
              <div
                className='flex h-12 w-full items-center justify-center  lg:hidden'
                onClick={() => {
                  // scroll down 200px the payment-info-modal element
                  const modal = document.getElementById('payment-info-modal');
                  if (modal) {
                    modal.scrollTo({
                      top: modal.scrollHeight,
                      behavior: 'smooth',
                    });
                  }
                }}
              >
                <ArrowDownIcon className='h-6 w-6 text-brand-500' />
              </div>
              <ProductPaymentTerms disableTitle />
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </section>
  );
}
